import { useState, useEffect } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shadcn-in-nx/ui/tooltip";
import { FileText, Download, ExternalLink, Receipt, FileCheck } from "lucide-react";
import { Bluelagoon } from "@bluelagoon";

function formatCurrency(amount, currency = "usd") {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(amount / 100);
}

function formatDate(timestamp) {
    if (!timestamp) return "-";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function getStatusBadge(status, t) {
    const statusConfig = {
        paid: { label: t?.("billing.invoiceStatus.paid") || "Paid", className: "bg-green-100 text-green-800" },
        open: { label: t?.("billing.invoiceStatus.open") || "Open", className: "bg-blue-100 text-blue-800" },
        draft: { label: t?.("billing.invoiceStatus.draft") || "Draft", className: "bg-gray-100 text-gray-800" },
        void: { label: t?.("billing.invoiceStatus.void") || "Void", className: "bg-gray-100 text-gray-500" },
        uncollectible: { label: t?.("billing.invoiceStatus.uncollectible") || "Uncollectible", className: "bg-red-100 text-red-800" },
    };
    const config = statusConfig[status] || { label: status, className: "bg-gray-100 text-gray-800" };
    return <Badge className={config.className}>{config.label}</Badge>;
}

function getTypeBadge(type, t) {
    if (type === "receipt") {
        return <Badge className="bg-purple-100 text-purple-800">{t?.("billing.oneTimePurchase") || "One-time"}</Badge>;
    }
    return null;
}

export function InvoicesTab({ user, t }) {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [paginationError, setPaginationError] = useState(null);
    const [retryKey, setRetryKey] = useState(0);

    const fetchMoreInvoices = async (createdBefore) => {
        try {
            setLoadingMore(true);
            setPaginationError(null);

            const response = await Bluelagoon.getInvoices({
                userId: user?.id,
                createdBefore,
            });
            const data = response?.data?.data || response?.data;

            const apiError = response?.data?.error || response?.error || data?.error;
            if (apiError) {
                throw new Error(apiError);
            }

            if (data?.invoices) {
                setInvoices((prev) => [...prev, ...data.invoices]);
                setHasMore(data.hasMore);
            }
        } catch (err) {
            console.error("Error fetching more invoices:", err);
            setPaginationError(err.message || "Failed to load more invoices");
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        let isCancelled = false;

        const loadInvoices = async () => {
            if (!user?.id) return;

            try {
                setLoading(true);
                setError(null);

                const response = await Bluelagoon.getInvoices({ userId: user.id });

                if (isCancelled) return;

                const data = response?.data?.data || response?.data;

                const apiError = response?.data?.error || response?.error || data?.error;
                if (apiError) {
                    throw new Error(apiError);
                }

                if (data?.invoices) {
                    setInvoices(data.invoices);
                    setHasMore(data.hasMore);
                } else {
                    setInvoices([]);
                    setHasMore(false);
                }
            } catch (err) {
                if (isCancelled) return;
                console.error("Error fetching invoices:", err);
                setError(err.message || "Failed to load invoices");
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        loadInvoices();

        return () => {
            isCancelled = true;
        };
    }, [user?.id, retryKey]);

    const handleLoadMore = () => {
        if (invoices.length > 0 && (hasMore || paginationError)) {
            const lastInvoice = invoices[invoices.length - 1];
            fetchMoreInvoices(lastInvoice.created);
        }
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p className="mt-2 text-gray-600">{t?.("billing.loadingInvoices") || "Loading invoices..."}</p>
                </div>
            </div>
        );
    }

    if (error && invoices.length === 0) {
        return (
            <div className="max-w-4xl mx-auto">
                <Card className="p-8 text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <UiButton onClick={() => setRetryKey((k) => k + 1)} variant="outline">
                        {t?.("common.tryAgain") || "Try Again"}
                    </UiButton>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h2 className="text-2xl font-bold">{t?.("billing.invoices") || "Invoices"}</h2>
                <p className="text-gray-600 text-sm mt-1">
                    {t?.("billing.viewInvoiceHistory") || "View your billing history and download invoices"}
                </p>
            </div>

            {invoices.length === 0 ? (
                <Card className="p-8 text-center">
                    <Receipt className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t?.("billing.noInvoices") || "No invoices yet"}</h3>
                    <p className="text-gray-600">
                        {t?.("billing.noInvoicesDescription") || "Your invoice history will appear here once you make a payment"}
                    </p>
                </Card>
            ) : (
                <>
                    <div className="space-y-4">
                        {invoices.map((invoice) => (
                            <Card key={invoice.id} className="p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            invoice.type === "receipt" ? "bg-purple-100" : "bg-blue-100"
                                        }`}>
                                            {invoice.type === "receipt" ? (
                                                <Receipt className="w-5 h-5 text-purple-600" />
                                            ) : (
                                                <FileText className="w-5 h-5 text-blue-600" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">
                                                    {invoice.number || (invoice.type === "receipt" ? t?.("billing.receipt") || "Receipt" : `Invoice ${invoice.id.slice(-8)}`)}
                                                </span>
                                                {getStatusBadge(invoice.status, t)}
                                                {getTypeBadge(invoice.type, t)}
                                            </div>
                                            <p className="text-sm text-gray-600 mt-0.5">{invoice.description}</p>
                                            <p className="text-xs text-gray-500 mt-1">{formatDate(invoice.created)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="font-semibold">{formatCurrency(invoice.amount, invoice.currency)}</p>
                                            {invoice.status === "paid" && invoice.paidAt && (
                                                <p className="text-xs text-gray-500">
                                                    {t?.("billing.paidOn") || "Paid"} {formatDate(invoice.paidAt)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex gap-1">
                                            <TooltipProvider delayDuration={200}>
                                                {invoice.receiptUrl && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <a
                                                                href={invoice.receiptUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                            >
                                                                <FileCheck className="w-4 h-4" />
                                                            </a>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{t?.("billing.viewReceipt") || "View Receipt"}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {invoice.invoicePdf && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <a
                                                                href={invoice.invoicePdf}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </a>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{t?.("billing.downloadPdf") || "Download PDF"}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {invoice.hostedInvoiceUrl && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <a
                                                                href={invoice.hostedInvoiceUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </a>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>{t?.("billing.viewInvoice") || "View Invoice"}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </TooltipProvider>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {(hasMore || paginationError) && (
                        <div className="mt-6 text-center">
                            {paginationError && (
                                <p className="text-red-600 text-sm mb-2">{paginationError}</p>
                            )}
                            <UiButton onClick={handleLoadMore} disabled={loadingMore} variant="outline">
                                {loadingMore
                                    ? t?.("common.loading") || "Loading..."
                                    : paginationError
                                        ? t?.("common.tryAgain") || "Try Again"
                                        : t?.("billing.loadMore") || "Load More"}
                            </UiButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
